import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _excluded = ["children", "isDisabled", "disabled", "id"];
var _templateObject;
import styled from "@emotion/styled";
import { usePrevious } from "@react-hookz/web";
import { motion } from "@smartsheet/design-tokens";
import { genId, useForkRef } from "@smartsheet/lodestar-utils";
import { callAllHandlers } from "@smartsheet/lodestar-utils";
import { useCombinedThemeMode } from "@smartsheet/sds-theme-internal";
import React, { forwardRef, useEffect, useMemo, useRef } from "react";
import { createFocusRingStyles } from "../../util/createFocusRingStyles";
import { withFocusToggleClassName } from "../../util/withFocusClassName";
import { useTabsContext } from "./TabsContext";
import { tabsTheme } from "./tabsTheme";
export var StyledTab = withFocusToggleClassName(styled("button")(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    background-color: transparent;\n    border: none;\n    color: ", ";\n    transition: color ", ";\n    font-size: 14px;\n    font-weight: ", ";\n    // Override chrome's ua stylesheet which specifies buttons use Arial (wut?).\n    font-family: inherit;\n    line-height: ", ";\n    margin: 0;\n    padding: 0px;\n    height: 100%;\n    cursor: pointer;\n\n    // handling for added icons\n    display: flex;\n    align-items: center;\n    gap: 4px;\n\n    // This is to ensure these tabs appear on top of the Underlines.\n    position: relative;\n    z-index: 2;\n\n    :not(:first-of-type) {\n        margin-left: ", ";\n    }\n\n    &:active {\n        color: ", ";\n    }\n\n    ", "\n\n    ", ";\n\n    &:focus::before {\n        // Customize the focusRingStyles from above.\n        bottom: 0;\n        border-bottom-left-radius: ", ";\n        border-bottom-right-radius: ", ";\n        // Customize the top padding when focused in Ursa.\n        inset-block-start: ", ";\n    }\n\n    &[aria-disabled=\"true\"] {\n        color: ", ";\n        cursor: not-allowed;\n    }\n"])), function (_ref) {
  var themeMode = _ref.themeMode,
    renderOn = _ref.renderOn;
  return tabsTheme[renderOn].textColor(themeMode);
}, motion.animation.default, function (_ref2) {
  var themeMode = _ref2.themeMode;
  return tabsTheme.font.weight(themeMode);
}, function (_ref3) {
  var themeMode = _ref3.themeMode;
  return tabsTheme.font.lineHeight(themeMode);
}, function (_ref4) {
  var themeMode = _ref4.themeMode;
  return tabsTheme.tabMarginLeft(themeMode);
}, function (_ref5) {
  var themeMode = _ref5.themeMode,
    renderOn = _ref5.renderOn;
  return tabsTheme[renderOn].textColor(themeMode);
}, function (_ref6) {
  var selected = _ref6.selected,
    themeMode = _ref6.themeMode;
  if (selected) {
    return {
      color: tabsTheme.selectedTextColor(themeMode)
    };
  }
}, function (_ref7) {
  var themeMode = _ref7.themeMode;
  return createFocusRingStyles({
    theme: themeMode,
    componentRadius: tabsTheme.focusRingRadius(themeMode),
    gap: tabsTheme.focusRingGap(themeMode),
    componentBorderWidth: 0,
    secondaryWidth: 2
  });
}, function (_ref8) {
  var themeMode = _ref8.themeMode;
  return tabsTheme.focusRingRadiusOverride(themeMode);
}, function (_ref9) {
  var themeMode = _ref9.themeMode;
  return tabsTheme.focusRingRadiusOverride(themeMode);
}, function (_ref10) {
  var themeMode = _ref10.themeMode;
  return tabsTheme.focusRingInsetOverride(themeMode);
}, function (_ref11) {
  var themeMode = _ref11.themeMode;
  return tabsTheme.disabledTextColor(themeMode);
}));
StyledTab.displayName = "StyledTab";
export var Tab = /*#__PURE__*/forwardRef(function (_ref12, parentRef) {
  var _thisReg$panelId;
  var children = _ref12.children,
    isDisabled = _ref12.isDisabled,
    disabled = _ref12.disabled,
    id = _ref12.id,
    rest = _objectWithoutProperties(_ref12, _excluded);
  var themeMode = useCombinedThemeMode();
  var _useTabsContext = useTabsContext(),
    _0 = _useTabsContext.onlyRenderActiveTabPanelChildren,
    onChange = _useTabsContext.onChange,
    renderOn = _useTabsContext.renderOn,
    registerTab = _useTabsContext.registerTab,
    selectedId = _useTabsContext.selectedId,
    manual = _useTabsContext.manual,
    focusPreviousTab = _useTabsContext.focusPreviousTab,
    focusNextTab = _useTabsContext.focusNextTab,
    tabRegistrations = _useTabsContext.tabRegistrations;
  var finalId = useMemo(function () {
    return id !== null && id !== void 0 ? id : genId("sds--tab-id");
  }, [id]);
  var localRef = useRef(null);
  var ref = useForkRef(localRef, parentRef);
  var thisReg = tabRegistrations.find(function (_ref13) {
    var tabId = _ref13.tabId;
    return tabId === finalId;
  });
  useEffect(function () {
    var _registerTab = registerTab(finalId, localRef),
      unregister = _registerTab.unregister;
    return unregister;
  }, [finalId, localRef, registerTab]);

  // If the selectedId changes and this tab is the newly selected tab, focus it.
  // This method allows the user to control the selected state of the tab from outside.
  var previousSelectedId = usePrevious(selectedId);
  useEffect(function () {
    if (
    // Note - this should only be run AFTER the initial render, otherwise it could cause
    // focus issues where a tab is focused twice which introduces oddities with the underline.
    previousSelectedId && previousSelectedId !== selectedId && selectedId === finalId && !manual && localRef.current !== document.activeElement) {
      var _localRef$current;
      (_localRef$current = localRef.current) === null || _localRef$current === void 0 ? void 0 : _localRef$current.focus();
    }
  }, [finalId, previousSelectedId, selectedId, manual, thisReg === null || thisReg === void 0 ? void 0 : thisReg.tabRef]);
  var isSelected = finalId === selectedId;

  /**
   * TODO: remove this in a future release with this ticket:
   * https://git.lab.smartsheet.com/team-ui-engineering/lodestar-core/-/issues/466
   */
  isDisabled = isDisabled || disabled;
  if (process.env.NODE_ENV !== "production") {
    if (disabled !== undefined) {
      console.warn("The 'disabled' prop has been deprecated in favor of 'isDisabled'. Please use that instead.");
    }
  }
  var onClick = callAllHandlers(function () {
    if (!isSelected && !isDisabled) {
      onChange(finalId);
    }
  }, rest.onClick);
  var onKeyDown = callAllHandlers(function (e) {
    switch (e.key) {
      case "ArrowLeft":
        focusPreviousTab();
        break;
      case "ArrowRight":
        focusNextTab();
        break;
    }
  }, rest.onKeyDown);
  return /*#__PURE__*/React.createElement(StyledTab, _extends({}, rest, {
    id: finalId,
    ref: ref,
    selected: isSelected,
    themeMode: themeMode,
    onClick: onClick,
    onKeyDown: onKeyDown,
    "aria-disabled": isDisabled,
    "aria-controls": (_thisReg$panelId = thisReg === null || thisReg === void 0 ? void 0 : thisReg.panelId) !== null && _thisReg$panelId !== void 0 ? _thisReg$panelId : undefined,
    "aria-selected": isSelected,
    renderOn: renderOn,
    tabIndex: isSelected ? 0 : -1,
    role: "tab"
  }), children);
});
Tab.displayName = "Tab";
//# sourceMappingURL=Tab.js.map